import omit from 'lodash/omit';
import cloneDeep from 'lodash/cloneDeep';

import * as BookmarkTypes from 'common/types/reports/bookmark';
import {FilterClause, GroupClause} from 'common/report/insights/util/types';
import {handleListReorder} from 'common/components/draggable-list/utils';

import {getMetricAtIndex} from '../helpers';
import {BookmarkDragOperationProps} from './types';
import {generateFilter, getBehaviorForDragDropOperation} from './utils';

function generateBreakdownFromFilter(filter: FilterClause, opts: {isCloning?: boolean} = {}) {
  const breakdown: GroupClause = {
    ...omit(cloneDeep(filter), `filterDateUnit`, `filterJoinType`, `filterOperator`, `filterType`, `filterValue`),

    profileType: undefined,
    joinPropertyType: filter.filterJoinType,
    propertyType: filter.defaultType,
    typeCast: filter.filterType,
    unit: filter.filterDateUnit,
  };

  // remove hidden or inherited fields from board filter/breakdown
  const forceClone = `isInherited` in breakdown && breakdown.isInherited;
  delete breakdown.isInherited;
  delete breakdown.isHidden;

  return {
    result: breakdown,
    forceClone: forceClone || opts.isCloning,
  };
}

/**
 * Handles rearranging the global filters section when dragging and dropping filters.
 */
export function handleGlobalFiltersRearrange(props: BookmarkDragOperationProps) {
  handleListReorder({
    event: props.event,
    entries: props.params.sections.filter!,
    entryGenerator: (filter, opts) => generateFilter(filter, opts)?.result,
    setEntries: (updatedEntries) => {
      props.updateParams((draft) => {
        draft.sections.filter = updatedEntries;
      });
    },
  });
}

/**
 * Handles dropping an inline filter from a metric onto the breakdown section
 */
export function handleInlineFilterToGlobalFilters(props: BookmarkDragOperationProps) {
  props.updateParams((params) => {
    const sourceClause = getMetricAtIndex({
      index: props.sourceData.metricIndex!,
      parentMetricIndex: props.sourceData.parentMetricIndex,
      params,
      type: BookmarkTypes.TopLevelMetricType.Metric,
    });

    if (!sourceClause || !sourceClause.behavior?.behaviors) {
      throw new Error(`Invalid source clause for filter drop: ${props.sourceData.group}`);
    }

    const sourceBehavior = getBehaviorForDragDropOperation(sourceClause, props.sourceData)!;
    const sourceFilter = sourceBehavior?.filters?.[props.sourceData.index] as FilterClause;
    const filterDetail = generateFilter(sourceFilter, {isCloning: props.isCloning});

    if (!filterDetail) {
      return;
    }

    params.sections.filter?.push(filterDetail.result);

    // removes inline filter from metric
    if (!filterDetail.forceClone) {
      sourceBehavior.filters?.splice(props.sourceData.index, 1);
    }
  });
}

/**
 * Handles dropping a global filter onto the breakdown section
 */
export function handleGlobalFilterToBreakdowns(props: BookmarkDragOperationProps) {
  props.updateParams((params) => {
    const sourceFilter = generateFilter(props.params.sections.filter![props.sourceData.index], {
      isCloning: props.isCloning,
    });

    if (!sourceFilter?.result) {
      return;
    }

    params.sections.group?.push(generateBreakdownFromFilter(sourceFilter.result).result);

    if (!sourceFilter.forceClone) {
      params.sections.filter!.splice(props.sourceData.index, 1);
    }
  });
}

/**
 * Handles dropping an inline filter from a metric onto the breakdown section
 */
export function handleInlineFilterToBreakdowns(props: BookmarkDragOperationProps) {
  props.updateParams((params) => {
    const sourceClause = getMetricAtIndex({
      index: props.sourceData.metricIndex!,
      parentMetricIndex: props.sourceData.parentMetricIndex,
      params,
      type: BookmarkTypes.TopLevelMetricType.Metric,
    });

    if (!sourceClause || !sourceClause.behavior?.behaviors) {
      throw new Error(`Invalid source clause for filter drop: ${props.sourceData.group}`);
    }

    const sourceBehavior = getBehaviorForDragDropOperation(sourceClause, props.sourceData)!;
    const sourceFilter = sourceBehavior?.filters?.[props.sourceData.index] as FilterClause;

    if (!sourceFilter) {
      return;
    }

    const breakdownDetail = generateBreakdownFromFilter(sourceFilter);
    params.sections.group?.push(breakdownDetail.result);

    // removes inline filter from metric
    if (!breakdownDetail.forceClone) {
      sourceBehavior.filters?.splice(props.sourceData.index, 1);
    }
  });
}
